/* ============================================================
   LD CAKE FILE — the booking-scoped cake layer (Toolkit Fix 01)
   File: assets/learn-cakefile.js
   Loaded on every Learn page via sections/learn-subnav.liquid,
   BEFORE learn-components.js and every tool script (all defer,
   document order), so the store is normalized to v2 before any
   consumer reads it.

   THE ONE RULE (fix brief 2026-07-16): when a booking is active,
   the booking record is the only source of truth for the cake.
   The old global slot (layerd-learn-tiers) survives purely as an
   anonymous scratch pad for visitors with no client files.

   Storage owned here:
     layerd-academy-bookings        the client-file store (v2)
     layerd-academy-bookings-v1-backup   raw v1 snapshot, written ONCE
     layerd-learn-active-booking   the global "which client" pointer
                                    (its own key on purpose: picker
                                    clicks never rewrite the store,
                                    and an old-tab clobber can't
                                    destroy the pointer)
     layerd-learn-tiers             the anonymous scratch slot

   v2 booking shape (v1 + additive):
     tierSpec: [{d, h, shape, layers, flavour}]   geometry lives
       per entry now (h/shape added; layers/flavour were already
       there). tiers count + tall stay, synced on write, so every
       existing reader keeps working.
     cake: { updatedAt, updatedBy }   provenance. updatedAt 0 =
       never explicitly set (geometry is preset-derived) — tools
       show the empty-state prompt instead of presenting presets
       as client data. updatedBy uses the suite tool handles.

   Events: 'layerd:cakefile' (detail {type, source, bookingId})
   plus the legacy 'layerd:cake-updated' so existing chip
   refreshers keep working mid-refactor.
   ============================================================ */
(function () {
  'use strict';

  var STORE_KEY = 'layerd-academy-bookings';
  var BACKUP_KEY = 'layerd-academy-bookings-v1-backup';
  var ACTIVE_KEY = 'layerd-learn-active-booking';
  var SCRATCH_KEY = 'layerd-learn-tiers';
  var EVT = 'layerd:cakefile';
  var LEGACY_EVT = 'layerd:cake-updated';

  /* the Planner's canonical presets — MUST stay byte-identical to
     tierSpecOf in learn-timeline-hub.js (the standing rule: any reader
     of a booking's tiers replicates this padding, never invents). */
  var TIER_PRESETS = { 1: [8], 2: [8, 6], 3: [10, 8, 6], 4: [12, 10, 8, 6], 5: [12, 10, 8, 6, 4], 6: [14, 12, 10, 8, 6, 4] };

  function parse(raw) { try { return JSON.parse(raw); } catch (e) { return null; } }
  function isArr(x) { return Object.prototype.toString.call(x) === '[object Array]'; }

  /* ---------- the canonical accessor (v2-aware tierSpecOf) ----------
     Returns COPIES with full geometry: {d, h, shape, layers, flavour}.
     Padding rule identical to the Planner's tierSpecOf; h defaults from
     the booking's tall flag, shape from the entry or round. */
  /* THE LAYER RULE (round 30, Mariya 2026-07-23) - one rule, one home.
     Her studio standard: a 4" tier is 3 layers, one more layer per extra
     inch. Height is the promise (it drives servings, price and structure);
     layers are the METHOD used to reach it, so they are DERIVED, never
     defaulted independently. Before this, the client file hardcoded 3
     layers whatever the height while Batch Math derived h-1 - so a 5"
     cake read as 3 layers on the file and 4 in Batch Math, and the file
     under-reported the bake by a third. */
  function standardLayers(h) { return Math.max(1, Math.round(+h || 4) - 1); }

  function specOf(b) {
    var n = Math.max(1, +b.tiers || 2);
    var src = isArr(b.tierSpec) ? b.tierSpec.slice(0, n) : [];
    var preset = TIER_PRESETS[n] || TIER_PRESETS[6];
    var defH = b.tall ? 6 : 4;
    var out = [];
    for (var i = 0; i < n; i++) {
      var s = src[i] || { d: preset[i] || 6, flavour: '' };
      var h = +s.h || defH;
      /* layers are derived from height UNLESS she deliberately overrode
         them (layersCustom). Stored layers written before this rule
         existed carry no flag, so they self-heal to the standard on the
         next read - which is exactly the 3-vs-4 bug being corrected. */
      var custom = !!s.layersCustom && s.layers != null;
      out.push({
        d: +s.d || preset[i] || 6,
        h: h,
        shape: s.shape === 'square' ? 'square' : s.shape === 'heart' ? 'heart' : 'round',
        layers: custom ? Math.max(1, +s.layers) : standardLayers(h),
        layersCustom: custom,
        flavour: s.flavour || '',
        /* r42 (Mariya, 2026-08-05): a FAUX tier — a dummy that gets sanded,
           sealed and covered but never baked or filled. It rides the spec so
           every tool reads it from the SAME place they already read d/h/
           shape: no servings (Serving), no batter or filling (Batch Math),
           its own early tasks (the Planner checklist), and its own priced
           line (Pricing). A faux tier still gets COVERED, so it must keep
           counting toward ganache/fondant coverage. */
        faux: !!s.faux
      });
    }
    return out;
  }
  /* did the booking carry any REAL (pre-padding) spec data? */
  function hadRealSpec(b) { return isArr(b.tierSpec) && b.tierSpec.length > 0; }

  /* ---------- scratch slot ---------- */
  function readScratchRaw() {
    var best = null;
    [localStorage, sessionStorage].forEach(function (store) {
      try {
        var v = JSON.parse(store.getItem(SCRATCH_KEY));
        if (v && isArr(v.tiers) && v.tiers.length && (!best || (v.at || 1) > (best.at || 1))) best = v;
      } catch (e) { /* private mode */ }
    });
    return best;
  }

  /* ---------- normalize: ANY raw value → an in-memory v2 store ----------
     Never returns a saveable-empty view of a non-empty store; never
     invents data. Returns null only when there is genuinely no store. */
  function normalizeBooking(b, migrationStamp) {
    /* materialize the padded spec ONCE so v2 entries carry geometry */
    var real = hadRealSpec(b);
    var spec = specOf(b);
    b.tierSpec = spec;
    b.tiers = spec.length;
    if (!b.cake || typeof b.cake.updatedAt !== 'number') {
      /* a real Planner-edited spec was explicitly set by the baker —
         stamp it so tools don't show a false "no tiers yet" prompt;
         0 is reserved for count-only bookings (preset-derived). */
      b.cake = real
        ? { updatedAt: migrationStamp, updatedBy: 'migration' }
        : { updatedAt: 0, updatedBy: null };
    }
    /* lifecycle fields (gating brief §8) — additive defaults only; a file
       that predates the field gets the honest empty, never a guess. An
       existing structure plan grandfathers structureConfirmed=true (the
       baker built it deliberately in the Structure tool). */
    /* r151 (Mariya: Serving said 26, Pricing said 28 for the same cake):
       HOW A CAKE IS SLICED IS PART OF THE CAKE. Until now `occasion` was
       per-tool UI state, so Serving's party count and Pricing's wedding
       default disagreed on a saved file. 'wedding' is the default every
       existing file has effectively been using, so no live file moves. */
    b.occasion = (b.occasion === 'party') ? 'party' : 'wedding';
    if (b.guests == null || isNaN(+b.guests)) b.guests = null;
    if (!b.deliveryFee || typeof b.deliveryFee !== 'object') {
      b.deliveryFee = { amount: (typeof b.deliveryFee === 'number' ? b.deliveryFee : null), overridden: false };
    }
    if (b.designElements == null) b.designElements = '';
    if (b.structureConfirmed == null) b.structureConfirmed = !!b.hardwareList;
    if (!isArr(b.changeLog)) b.changeLog = [];
    return b;
  }

  function adoptScratchIfSafe(store) {
    /* conservative adoption (brief §2.3 + the deterministic upgrade):
       a slot TAGGED to a booking id is not a guess — adopt it there.
       An untagged slot is adopted only in the exactly-one-booking case
       with a matching length (or no real spec to lose). */
    var slot = readScratchRaw();
    if (!slot) return;
    var target = null;
    if (slot.bookingId) {
      for (var i = 0; i < store.bookings.length; i++) {
        if (store.bookings[i].id === slot.bookingId) { target = store.bookings[i]; break; }
      }
    } else if (store.bookings.length === 1) {
      var only = store.bookings[0];
      if (slot.tiers.length === +only.tiers || !hadRealSpec(only)) target = only;
    }
    if (!target) return;
    if (target.cake && target.cake.updatedAt > 0) return;   /* never clobber explicit data */
    applyGeometry(target, slot.tiers);
    target.cake = { updatedAt: Date.now(), updatedBy: slot.from || 'unknown' };
  }

  /* positional geometry merge: layers/flavour preserved from the old
     entries; comboAssignments kept index-aligned (the Planner's splice
     rule, centralized): truncated on shrink, null-padded on grow. */
  function applyGeometry(b, tiers) {
    var old = isArr(b.tierSpec) ? b.tierSpec : [];
    var next = [];
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i] || {};
      var prev = old[i] || {};
      next.push({
        d: +t.d || 6,
        h: +t.h || 4,
        shape: t.shape === 'square' ? 'square' : t.shape === 'heart' ? 'heart' : 'round',
        layers: (prev.layers == null) ? 3 : +prev.layers,
        flavour: prev.flavour || ''
      });
    }
    b.tierSpec = next;
    b.tiers = next.length;
    b.tall = next.some(function (t) { return t.h >= 6; });
    if (isArr(b.comboAssignments)) {
      if (b.comboAssignments.length > next.length) b.comboAssignments = b.comboAssignments.slice(0, next.length);
      while (b.comboAssignments.length < next.length) b.comboAssignments.push(null);
    }
  }

  function normalize(raw) {
    var v = parse(raw);
    if (!v || !isArr(v.bookings)) return null;
    if (v.v !== 1 && v.v !== 2) return null;
    var stamp = Date.now();
    var fromV1 = v.v === 1;
    v.bookings.forEach(function (b) { normalizeBooking(b, stamp); });
    if (fromV1) adoptScratchIfSafe(v);
    v.v = 2;
    return v;
  }

  /* ---------- load-time migration (best-effort persist) ----------
     In-memory normalization ALWAYS works (readStore below normalizes on
     every call), so a failed persist degrades to re-migrating each
     load — never to an empty UI. Backup is WRITE-ONCE: after an old-tab
     clobber, re-migration must not overwrite the pristine original. */
  (function migrateOnLoad() {
    var raw;
    try { raw = localStorage.getItem(STORE_KEY); } catch (e) { return; }
    if (!raw) return;                       /* no store: do NOT materialize one */
    var v = parse(raw);
    if (!v || !isArr(v.bookings)) return;   /* corrupt: leave untouched for inspection */
    if (v.v === 1) {
      try { if (!localStorage.getItem(BACKUP_KEY)) localStorage.setItem(BACKUP_KEY, raw); } catch (e) { }
    }
    var norm = normalize(raw);
    if (norm) { try { localStorage.setItem(STORE_KEY, JSON.stringify(norm)); } catch (e) { } }
  })();

  /* ---------- store access: always fresh, always normalized ---------- */
  function readStore() {
    var raw;
    try { raw = localStorage.getItem(STORE_KEY); } catch (e) { raw = null; }
    var norm = raw ? normalize(raw) : null;
    return norm || { v: 2, bookings: [] };
  }
  /* fresh read → mutate → write. If an old tab clobbered back to v1,
     normalize() re-migrates before the mutation applies. */
  function update(fn) {
    var store = readStore();
    fn(store);
    try { localStorage.setItem(STORE_KEY, JSON.stringify(store)); } catch (e) { return false; }
    return true;
  }
  function bookingById(store, id) {
    for (var i = 0; i < store.bookings.length; i++) if (store.bookings[i].id === id) return store.bookings[i];
    return null;
  }

  /* ---------- events ---------- */
  function emit(type, source, bookingId) {
    try { window.dispatchEvent(new CustomEvent(EVT, { detail: { type: type, source: source || null, bookingId: bookingId || null } })); } catch (e) { }
    try { window.dispatchEvent(new Event(LEGACY_EVT)); } catch (e) { }
  }

  /* ---------- the active pointer ---------- */
  function getActiveId() {
    try { return localStorage.getItem(ACTIVE_KEY) || null; } catch (e) { return null; }
  }
  function setActiveBooking(id, source) {
    try {
      if (id) localStorage.setItem(ACTIVE_KEY, id);
      else localStorage.removeItem(ACTIVE_KEY);
    } catch (e) { }
    emit('active', source, id || null);
  }
  /* ---- r57 display gate (Mariya, 2026-08-19): "shouldn't it just have my
     stuff saved to my specific account?" A logged-out browser on a device
     that has SYNCED an account's data stops SHOWING that data — it is
     never touched, never deleted, and comes back at login. Two facts must
     both hold: tier is 'visitor' (genuinely logged out — a logged-in free
     or lapsed member is never gated), and layerd-cloud-seen is non-empty
     (only real account syncs ever write it, so a visitor who has never
     logged in — the free-slots funnel — is never gated either).
     Scope: the READ-for-display doors only. readStore stays ungated: it
     feeds WRITE paths, and an empty read there could persist an empty
     store over real bookings. */
  function displayGated() {
    try {
      var host = document.querySelector('[data-lh-tier]');
      if (!host || host.getAttribute('data-lh-tier') !== 'visitor') return false;
      var seen = localStorage.getItem('layerd-cloud-seen');
      if (!seen) return false;
      var v = JSON.parse(seen);
      return !!(v && Object.keys(v).length);
    } catch (e) { return false; }
  }

  /* ---- r201 the free tier's one-file cap (Mariya, 2026-09-01) ----
     "if they try to add a second they need to delete the first."

     The cap ALREADY existed in the Planner hub, and only there. The
     client bar's "+ New booking" popover and Serving's in-results
     callout both called createInquiry with no tier check, so a free
     member could hold as many files as she liked by starting them from
     any tool that was not the Planner. Every creation door in the suite
     funnels through createInquiry, so the guard belongs HERE - it closes
     the two leaks that existed and any door added later.

     COUNTS EVERY BOOKING, not activeFiles(): that helper drops archived
     and past-dated files, and a cap the member can clear by archiving is
     not a cap. Deleting is the way through, which is the intent.

     GATES ADDING, NEVER READING OR EDITING - the r53 recipe-slots rule.
     A lapsed Pro with six files keeps all six fully usable; she just
     cannot start a seventh.

     Tier comes from the subnav's data-lh-tier (the same attribute
     displayGated reads just above). 'premium' covers Academy too - the
     gate snippet maps both tags to it. A visitor reads 'visitor' and is
     capped at one, which matches the Planner, where a logged-out page
     has no #ldtl-premium island and so runs as free. */
  var FREE_MAX_FILES = 1;
  function tierOf() {
    try {
      var host = document.querySelector('[data-lh-tier]');
      var t = host && host.getAttribute('data-lh-tier');
      return (t === 'free' || t === 'premium' || t === 'visitor') ? t : 'visitor';
    } catch (e) { return 'visitor'; }
  }
  function fileCap() { return tierOf() === 'premium' ? Infinity : FREE_MAX_FILES; }
  function fileCount() { return (readStore().bookings || []).length; }
  function atCap() { return fileCount() >= fileCap(); }

  function getActiveBooking() {
    if (displayGated()) return null;   /* r57: hidden while logged out */
    var id = getActiveId();
    if (!id) return null;
    var b = bookingById(readStore(), id);
    if (!b || b.status === 'archived') { setActiveBooking(null, 'cakefile'); return null; }
    return b;
  }
  /* tool-load scope resolution: an explicit ?booking= link wins and SETS
     the pointer; a param naming a missing booking falls back silently.
     No param → the remembered pointer. */
  function resolveScope() {
    /* r57: gated = unscoped, and WITHOUT touching the remembered pointer —
       clearing it here would mutate state the gate promises to leave
       alone; login must restore exactly what she had. */
    if (displayGated()) return null;
    var m = location.search.match(/[?&]booking=([^&]+)/);
    if (m) {
      var id; try { id = decodeURIComponent(m[1]); } catch (e) { id = m[1]; }
      var b = bookingById(readStore(), id);
      if (b && b.status !== 'archived') { setActiveBooking(id, 'url'); return b; }
      setActiveBooking(null, 'url');
      return null;
    }
    /* round eighteen (Mariya): a delivered or archived cake never stays
       SILENTLY scoped — past-delivery files drop from every picker, so the
       ambient pointer must let go too or the page keeps claiming a client
       the picker won't show. A deliberate ?booking= link (above) still
       opens past files for looking back. */
    var cur = getActiveBooking();
    if (cur) {
      var t = new Date();
      var tl = t.getFullYear() + '-' + ('0' + (t.getMonth() + 1)).slice(-2) + '-' + ('0' + t.getDate()).slice(-2);
      if (cur.status === 'archived' || (cur.date && cur.date < tl)) {
        setActiveBooking(null, 'expired');
        return null;
      }
    }
    return cur;
  }

  /* ---------- the cake API ---------- */
  function readCake() {
    var b = getActiveBooking();
    if (b) {
      var tiers = specOf(b);
      var meta = b.cake || { updatedAt: 0, updatedBy: null };
      meta = { updatedAt: meta.updatedAt, updatedBy: meta.updatedBy,
               occasion: b.occasion === 'party' ? 'party' : 'wedding' };
      var source = !tiers.length ? 'empty' : (meta.updatedAt > 0 ? 'booking' : 'derived');
      return { tiers: tiers, source: source, booking: b, meta: meta };
    }
    var slot = readScratchRaw();
    /* a slot still tagged to some client (legacy working-override) is not
       an anonymous scratch cake — don't present it as one */
    if (slot && !slot.bookingId) {
      var st = slot.tiers.map(function (t) {
        return { d: +t.d || 6, h: +t.h || 4, shape: t.shape === 'square' ? 'square' : t.shape === 'heart' ? 'heart' : 'round', layers: 3, flavour: '' };
      });
      return { tiers: st, source: 'scratch', booking: null, meta: { updatedAt: slot.at || 0, updatedBy: slot.from || null, occasion: slot.occasion === 'party' ? 'party' : 'wedding' } };
    }
    return { tiers: [], source: 'empty', booking: null, meta: { updatedAt: 0, updatedBy: null } };
  }

  /* SPINE r240: the anonymous scratch cake, INDEPENDENT of any active booking.
     readCake() prefers the active booking, so the workspace's Quick-tools mode
     (which always writes the scratch slot) needs a scratch-only read or its
     occasion toggle and add-tier appear to do nothing. Same shape as the
     scratch branch of readCake. */
  function readScratch() {
    var slot = readScratchRaw();
    if (slot && !slot.bookingId) {
      var st = slot.tiers.map(function (t) {
        return { d: +t.d || 6, h: +t.h || 4, shape: t.shape === 'square' ? 'square' : t.shape === 'heart' ? 'heart' : 'round', layers: 3, flavour: '' };
      });
      return { tiers: st, source: 'scratch', booking: null, meta: { updatedAt: slot.at || 0, updatedBy: slot.from || null, occasion: slot.occasion === 'party' ? 'party' : 'wedding' } };
    }
    return { tiers: [], source: 'empty', booking: null, meta: { updatedAt: 0, updatedBy: null, occasion: 'wedding' } };
  }

  function dimsSig(spec) {
    return (isArr(spec) ? spec : []).map(function (t) {
      return (+t.d || 0) + '\u2033\u00d7' + (+t.h || 4) + '\u2033';
    }).join(' / ');
  }
  /* r127 (Mariya: "the batch math should always automate based on the
     sizing saved"). A resize used to stamp the whole sheet stale and send
     her back to Batch Math by hand. Most of it can simply follow the cake:
     `inputs` is recomputable, and the outputs snapshot is a pure function
     of it. The exception is a calculator whose tiers she typed herself -
     detected by comparing its saved tiers against the OLD geometry.

     This only MARKS. The engine is not loaded on Serving or Pricing, so
     the first reader that has it heals the sheet. */
  function tiersSigOf(list) {
    return (isArr(list) ? list : []).map(function (t) {
      return (+t.d || 0) + '\u2033\u00d7' + (+t.h || 4) + '\u2033';
    }).join(' / ');
  }
  function markBatchSheetForResize(b, beforeSig) {
    var sheet = b.batchSheet;
    if (!sheet) return;
    var anyCustom = false, anyTracking = false;
    ['ct', 'ga', 'fl', 'bc'].forEach(function (tool) {
      var entry = sheet[tool];
      if (!entry || !entry.inputs || entry.skipped) return;
      if (tiersSigOf(entry.inputs.tiers) === beforeSig) {
        entry.inputs.tiers = (isArr(b.tierSpec) ? b.tierSpec : []).map(function (t) {
          return { d: +t.d || 6, h: +t.h || 4, shape: t.shape || 'round',
            layers: (t.layers == null ? 3 : +t.layers) };
        });
        entry._recompute = true;
        delete entry._custom;
        anyTracking = true;
      } else {
        entry._custom = true;          /* her own tiers - never overwritten */
        anyCustom = true;
      }
    });
    /* "stale" now means only: something here still needs HER */
    if (anyCustom) sheet._stale = true; else delete sheet._stale;
    if (anyTracking) sheet._resized = true;
  }
  var BOOKED_PLUS = { booked: 1, production_ready: 1, delivered: 1 };
  var OCCASIONS = { wedding: 1, party: 1 };
  /* r151: opts is ADDITIVE - every existing writeCake(tiers, from) call
     keeps working unchanged and simply leaves the occasion alone. */
  function writeCake(tiers, fromTool, opts) {
    var id = getActiveId();
    if (!id || !isArr(tiers) || !tiers.length) return null;
    var occ = opts && OCCASIONS[opts.occasion] ? opts.occasion : null;
    var saved = null;
    var ok = update(function (store) {
      var b = bookingById(store, id);
      if (!b) return;
      var before = dimsSig(b.tierSpec);
      applyGeometry(b, tiers);
      if (occ) b.occasion = occ;
      b.cake = { updatedAt: Date.now(), updatedBy: fromTool || 'unknown' };
      /* size-edit cascade on a live booking (gating brief §6): the change
         is allowed — the tools' own explicit save prompts are the
         confirmation — but downstream derivations honestly reset and the
         change is logged immutably. */
      var after = dimsSig(b.tierSpec);
      if (BOOKED_PLUS[b.status] && before !== after && before) {
        b.structureConfirmed = false;
        if (b.batchSheet) markBatchSheetForResize(b, before);
        if (!isArr(b.changeLog)) b.changeLog = [];
        b.changeLog.push({ field: 'size', from: before, to: after, timestamp: Date.now(), by: fromTool || 'unknown' });
      }
      saved = b;
    });
    if (!ok || !saved) return null;
    emit('cake', fromTool, id);
    return { ok: true, booking: saved };
  }

  /* r151: changing the pills is not a geometry edit - this writes the one
     field without touching tierSpec or the size-change cascade. */
  function writeOccasion(occasion, fromTool) {
    var id = getActiveId();
    if (!id || !OCCASIONS[occasion]) return false;
    var ok = update(function (store) {
      var b = bookingById(store, id);
      if (!b || b.occasion === occasion) return;
      b.occasion = occasion;
    });
    if (ok) emit('occasion', fromTool, id);
    return !!ok;
  }

  function writeScratch(tiers, fromTool, opts) {
    if (!isArr(tiers) || !tiers.length) return false;
    var payload = JSON.stringify({
      tiers: tiers.map(function (t) {
        return { d: +t.d || 6, h: +t.h || 4, shape: t.shape === 'square' ? 'square' : t.shape === 'heart' ? 'heart' : 'round' };
      }).sort(function (a, b) { return b.d - a.d; }),
      at: Date.now(), from: fromTool || 'unknown', bookingId: null,
      occasion: (opts && OCCASIONS[opts.occasion]) ? opts.occasion : undefined
    });
    var ok = false;
    try { localStorage.setItem(SCRATCH_KEY, payload); ok = true; } catch (e) { }
    try { sessionStorage.setItem(SCRATCH_KEY, payload); ok = true; } catch (e) { }
    if (ok) emit('scratch', fromTool, null);
    return ok;
  }

  function onChange(cb) {
    window.addEventListener(EVT, function (e) { cb(e.detail || {}); });
    window.addEventListener('storage', function (e) {
      if (!e.key || e.key === STORE_KEY || e.key === SCRATCH_KEY || e.key === ACTIVE_KEY) {
        cb({ type: 'storage', source: null, bookingId: null, key: e.key || null });
      }
    });
  }

  /* Fix 03 follow-up (2026-07-17): any tool can start a client file — the
     Serving page is the suite's free front door, and a visitor's first
     calculation must not dead-end there. The field set mirrors the
     Planner's own create (its engine expects every key), status starts at
     inquiry, and the new file becomes the active client so the pipeline
     bar appears at once. */
  function createInquiry(name, fromTool, opts) {
    /* r201: the backstop. Callers should ask atCap() first and show the
       pitch; this makes sure one that forgets cannot write the file
       anyway. Returning null is the existing failure contract - every
       call site already branches on a falsy return. */
    if (atCap()) return null;
    function p2(n) { return (n < 10 ? '0' : '') + n; }
    var today = new Date();
    var created = null;
    var ok = update(function (store) {
      var b = {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        name: (name || '').trim(),
        created: today.getFullYear() + '-' + p2(today.getMonth() + 1) + '-' + p2(today.getDate()),
        /* r44 (Mariya, 2026-08-12): "it didn't ask me to enter an event date
           anywhere ... This should be mandatory." Every creation path now
           passes one in. It stays a STRING here rather than a required
           argument so an older caller can't crash — but both UIs refuse to
           submit without it, which is where the rule belongs. */
        date: (opts && opts.date) ? String(opts.date) : '', time: '14:00', drive: 30, buffer: 0,
        tiers: 2, cover: 'buttercream', tall: false, venueSetup: false,
        /* r44: delivery is the default because most event cakes are
           delivered — pickup is the opt-out, not a second first-class mode. */
        pickup: false,
        flowers: false, band: 'bouquet', florals: false, paint: false,
        /* r200: hand-sculpting split off hand-painting (Pricing elements) */
        sculpt: false,
        compote: false, macarons: false, topper: false, chocdec: false,
        wafer: false, fruit: false, sheetcake: false,
        startFrom: '', blocked: [], fridge: 'comfortable', venue: '', notes: '',
        colorIndex: store.bookings.length % 8, components: [],
        status: 'inquiry', quotes: [], hardwareList: null, comboAssignments: null,
        tierSpec: null, cake: { updatedAt: 0, updatedBy: null }, occasion: 'wedding',
        guests: (opts && +opts.guests > 0) ? Math.round(+opts.guests) : null
      };
      /* r84 (Mariya, 2026-08-23): LINKED FILES — "what if he wants to add
         multiple cakes to one client file?" A linked cake is its own full
         file (own tiers, own quote, own batch sheet — every tool keeps its
         one-cake assumptions) sharing the client's event facts. The NAME
         carries the cake label ("Ana & Sam — Cake 2") so every list, task
         and legend in the suite distinguishes them with zero new call
         sites; `groupId` is what the file page's switcher groups on. */
      if (opts && opts.linkTo) {
        var src = null;
        for (var i = 0; i < store.bookings.length; i++) {
          if (store.bookings[i].id === opts.linkTo) { src = store.bookings[i]; break; }
        }
        if (src) {
          if (!src.groupId) src.groupId = src.id;
          b.groupId = src.groupId;
          var n = store.bookings.filter(function (x) {
            return (x.groupId || x.id) === src.groupId;
          }).length + 1;
          var base = ((src.name || '').trim() || 'Cake order').replace(/\s+— Cake \d+$/, '');
          b.name = base + ' — Cake ' + n;
          b.date = src.date || b.date; b.time = src.time || b.time;
          b.venue = src.venue || ''; b.drive = (src.drive != null) ? src.drive : b.drive;
          b.buffer = +src.buffer || 0; b.fridge = src.fridge || 'comfortable';
          b.blocked = (src.blocked || []).slice();
          b.pickup = !!src.pickup;
          b.status = src.status === 'booked' || src.status === 'production_ready' ? 'booked' : 'inquiry';
        }
      }
      normalizeBooking(b, Date.now());
      store.bookings.push(b);
      created = b;
    });
    if (!ok || !created) return null;
    setActiveBooking(created.id, fromTool || 'cakefile');
    emit('file', fromTool || 'cakefile', created.id);
    return created;
  }

  /* ============================================================
     THE PIPELINE — ONE definition, read by every renderer.
     (2026-08-11, after Mariya found a row dash that could never fill.)

     Before this block there were THREE step lists with THREE sets of
     predicates: PIPE_STEPS (the client-file chip dots, on every tool
     page), stepDashes (the Planner list row) and thisFileSteps (the
     file page's "N of N done"). They disagreed on FOUR of six —

       · batch     the chip checked only staleness; the Planner also
                   required size + flavours and honoured the confirm
                   signature, so a file read done on the chip and
                   not-done in the Planner
       · quote     the chip wanted a stored quote; the Planner also
                   accepted "paid in full"
       · flavours  the chip had her 2026-07-16 escape hatch (went
                   straight to Batch Math), the Planner did not
       · deposit   the chip used statusOf === 'booked' (so a delivered
                   cake dropped a step); the row read a RAW b.deposit
                   record nothing else in the suite tests — that was
                   the dash that could never fill

     This is the only place any of it is decided now. A renderer may
     choose which steps to SHOW, never what "done" means. Adding a
     step means adding it here, once.

     It lives in learn-cakefile.js because this is the shared owner of
     the booking record and the only module loaded on every page (via
     the learn-subnav section) — learn-components.js and the Planner
     both read from it, never the reverse.
     ============================================================ */
  function statusOf(b) { return (b && b.status) || 'booked'; }
  /* the three statuses that mean "this cake is really happening" */
  function isBooked(b) {
    var st = statusOf(b);
    return st === 'booked' || st === 'production_ready' || st === 'delivered';
  }
  function sizeConfirmed(b) { return !!(b && b.cake && b.cake.updatedAt > 0); }
  /* STRICT: every tier that NEEDS a flavour has one. The precondition other
     steps lean on — deliberately NOT the same thing as the Flavours step
     being satisfied (see flavoursDone).

     2026-08-12 BUG FIX: this used to test every tier including FAUX ones.
     A dummy is sanded, sealed and covered but never baked or filled, so it
     carries no flavour BY DESIGN (setFaux in academy-recipes.js blanks it) —
     which meant any cake with a dummy tier could never finish the Flavours
     step, ever. The dot stayed hollow, the Planner kept reporting "a tier is
     missing its flavour", and the file could never reach production-ready.
     Introduced with faux tiers in r42/r43 and invisible until the flavour
     warning was built on top of it. A cake of nothing but dummies has no
     flavours to set and is not "done" — hence the real-tier count check. */
  function everyTierHasFlavour(b) {
    if (!b) return false;
    var real = specOf(b).filter(function (t) { return !t.faux; });
    return real.length > 0 && real.every(function (t) { return ((t.flavour || '') + '').trim() !== ''; });
  }
  /* the tiers still waiting on a flavour — the warning copy needs to NAME
     them, and this is the one place that decides which tiers count */
  function tiersMissingFlavour(b) {
    if (!b) return [];
    var out = [];
    specOf(b).forEach(function (t, i) {
      if (t.faux) return;
      if (((t.flavour || '') + '').trim() === '') out.push({ index: i, d: t.d, label: 'Tier ' + (i + 1) + ' · ' + t.d + '″' });
    });
    return out;
  }
  function batchIsStale(b) { return !!(b && b.batchSheet && b.batchSheet._stale); }
  function batchHasFigures(b) {
    var sh = b && b.batchSheet;
    if (!sh) return false;
    var keys = ['ct', 'ga', 'fl', 'bc', 'sc'];
    for (var i = 0; i < keys.length; i++) {
      var o = sh[keys[i]] && sh[keys[i]].outputs;
      if (o && (o.total > 0 || o.batches > 0 || o.batchesQ > 0)) return true;
    }
    return false;
  }
  /* fingerprint of what was confirmed — if the figures move afterwards the
     confirmation lapses, the same shape as Structure's re-confirm */
  function batchSig(b) {
    var parts = [];
    ((b && b.components) || []).forEach(function (c) {
      parts.push(((c.name || '') + '').toLowerCase() + '=' + (c.batches == null ? '-' : c.batches));
    });
    parts.sort();
    var sh = (b && b.batchSheet && !b.batchSheet._stale) ? b.batchSheet : null;
    if (sh) {
      ['ct', 'ga', 'fl', 'bc', 'sc'].forEach(function (k) {
        var o = sh[k] && sh[k].outputs;
        if (o) parts.push(k + ':' + (o.total != null ? o.total : (o.batchesQ != null ? o.batchesQ : o.batches)));
      });
    }
    return parts.join('|');
  }

  /* ---- the six step predicates ---- */
  /* Her 2026-07-16 ruling stands: a one-flavour cake that went straight to
     Batch Math is not held hostage by this step. */
  function flavoursDone(b) {
    return everyTierHasFlavour(b) || (!batchIsStale(b) && batchHasFigures(b));
  }
  function structureDone(b) { return !!(b && b.structureConfirmed); }
  /* a quote OR the money actually in — a file paid in full without a stored
     quote is settled by any honest reading */
  function quoteDone(b) {
    if (b && b.quotes && b.quotes.length) return true;
    return !!(isBooked(b) && b.balancePaid && b.balancePaid.at);
  }
  /* Saving in Batch Math IS the human review (her 2026-08-03 ruling), so a
     saved non-stale sheet counts on its own; the explicit confirm survives
     for auto-derived numbers nobody looked at.
     DROPPED 2026-08-11: the old everyTierHasFlavour precondition. It
     contradicted the flavours escape hatch above (Flavours would read done
     while Batch read not-done for the very same cake), and it was
     belt-and-braces anyway — changing flavours or sizes already marks the
     sheet _stale, which fails on the first line. */
  function batchDone(b) {
    if (!b) return false;
    if (batchIsStale(b)) return false;
    if (!sizeConfirmed(b)) return false;
    if (batchHasFigures(b)) return true;
    if (!b.batchConfirmed) return false;
    if (b.batchConfirmed.sig && b.batchConfirmed.sig !== batchSig(b)) return false;
    return true;
  }
  /* the booking gate IS size + quote + deposit, so reaching a booked status
     is what "deposit taken" means. isBooked, not === 'booked', so a
     production-ready or delivered cake keeps the step rather than losing it. */
  function bookedDone(b) { return isBooked(b); }

  /* Order is HERS (the prototype flow she signed off): Serving → Structure
     → Flavours → Batch → Quote → Deposit. `match` is how the chip finds
     each step's subnav tab; `label` is the one word every renderer shows. */
  /* r150 (Mariya, 2026-08-26): "The progress bar has to follow the order
     of the nav bar." It now matches the toolkit grid exactly - Serving,
     Pricing, Structure, Batch Math, Recipes - and this supersedes the
     older "order is HERS (the prototype flow)" note above.
     Safe: every `done` predicate is independent of position, and
     batchDone needs size + figures, NOT flavoursDone, so Batch sitting
     ahead of Flavours can never make "Next:" point at an impossible
     step. Reordering here moves the chip dots, the Planner list rows and
     the file page's "N of N" together - that is the point of one list. */
  var PIPELINE = [
    { key: 'serving',   label: 'Serving',    match: /serving/i,           done: sizeConfirmed },
    { key: 'pricing',   label: 'Quote',      match: /pricing/i,           done: quoteDone },
    { key: 'structure', label: 'Structure',  match: /structure/i,         done: structureDone },
    /* r163 (Mariya: "batch math is coming before recipes. It has to be
       recipes and then batch math"). Recipes/flavours FEED Batch Math -
       you cannot scale a batch of a recipe you have not chosen - and
       batchDone's own staleness rule already treats a flavour change as
       reopening Batch. The order now matches the dependency.
       NOTE: the fallback literal in learn-components.js had these the
       right way round already, which is why this looked correct in code
       review and wrong on screen - THIS array is the one that renders. */
    { key: 'flavours',  label: 'Flavours',   match: /recipe/i,            done: flavoursDone },
    { key: 'batch',     label: 'Batch Math', match: /batch/i,             done: batchDone },
    { key: 'planner',   label: 'Booked',     match: /planner|timeline/i,  done: bookedDone }
  ];
  function pipelineFor(b) {
    return PIPELINE.map(function (s) {
      var ok = false;
      try { ok = !!s.done(b); } catch (e) { ok = false; }
      return { key: s.key, label: s.label, match: s.match, done: ok };
    });
  }
  function pipelineCount(b) {
    var st = pipelineFor(b);
    return { done: st.filter(function (s) { return s.done; }).length, total: st.length };
  }

  /* r199: the file page's design section writes through here — one writer
     per booking. Fresh read → mutate → write, so a stale caller can never
     clobber the store; emits the same change event every other write does. */
  function updateBooking(id, mutate) {
    if (!id || typeof mutate !== 'function') return false;
    var ok = update(function (store) {
      var b = bookingById(store, id);
      if (b) mutate(b);
    });
    if (ok) emit('update', 'cakefile', id);
    return ok;
  }
  /* SPINE r240: hard-delete a cake file (the workspace Bookings list needs it;
     the free one-file cap counts every booking, so deleting is the way through). */
  function deleteBooking(id) {
    if (!id) return false;
    var ok = update(function (store) {
      store.bookings = (store.bookings || []).filter(function (b) { return b.id !== id; });
    });
    if (ok) { if (getActiveId() === id) setActiveBooking(null, 'delete'); emit('file', 'delete', id); }
    return ok;
  }

  /* ---------- SPINE r230: the shared derived-money snapshot ----------
     cost/sv, charge/sv and the quote are computed ONLY by Pricing (its
     cost-first engine), but the spine's summary bar needs those numbers on
     every step and on pages where the Pricing script never loads. So Pricing
     writes a small snapshot onto the scoped booking on recompute, and any
     surface reads it back through readMoney(). SERVINGS is deliberately NOT
     stored - it is recomputed live from the cake through cakeCore, the one
     owner (SPINE r229), so it can never drift from the tiers. Unscoped (quick
     tools) writes nothing: the whole point is that nothing is saved without a
     client file. */
  function servingsOfBooking(b) {
    var C = window.LAYERD && window.LAYERD.cakeCore;
    if (!C || !C.servingsFor || !b) return 0;
    var occ = b.occasion === 'party' ? 'party' : 'wedding';
    var total = 0;
    specOf(b).forEach(function (t) {
      if (t.faux) return;
      var shape = t.shape === 'square' ? 'square' : (t.shape === 'heart' ? 'heart' : 'round');
      total += C.servingsFor(shape, +t.d, occ, +t.h || 4);
    });
    return total;
  }
  function num(v) { return (v == null || isNaN(+v)) ? null : +v; }
  function writeMoney(fromTool, money) {
    var id = getActiveId();
    if (!id || !money) return false;   /* unscoped = nothing saved */
    return updateBooking(id, function (b) {
      b.derived = {
        costSv: num(money.costSv),
        chargeSv: num(money.chargeSv),
        quote: num(money.quote),
        occasion: money.occasion || b.occasion || 'wedding',
        servingsAt: num(money.servings),   /* the count the quote was priced at — for staleness */
        from: fromTool || null,
        at: Date.now()
      };
    });
  }
  /* { servings, costSv, chargeSv, quote, occasion, priced, at } for the active
     booking, or null when unscoped. servings is always live; the money fields
     are null until Pricing has run once (priced=false), which is exactly how
     the bar should read before the Cost & price step is done. */
  function readMoney() {
    var b = getActiveBooking();
    if (!b) return null;
    var d = b.derived || null;
    var live = servingsOfBooking(b);
    var priced = !!(d && d.quote != null);
    /* the quote was priced at a serving count; if the cake has since been
       resized, the stored cost/charge/quote no longer match — say so rather
       than show a number that silently disagrees with the servings. */
    var stale = priced && d.servingsAt != null && d.servingsAt !== live;
    return {
      servings: live,
      costSv: d ? num(d.costSv) : null,
      chargeSv: d ? num(d.chargeSv) : null,
      quote: d ? num(d.quote) : null,
      occasion: b.occasion === 'party' ? 'party' : 'wedding',
      priced: priced,
      stale: stale,
      at: d ? (d.at || 0) : 0
    };
  }

  var api = {
    readStore: readStore,
    specOf: specOf,
    updateBooking: updateBooking,
    deleteBooking: deleteBooking,
    servingsOfBooking: servingsOfBooking,
    writeMoney: writeMoney,
    readMoney: readMoney,
    PIPELINE: PIPELINE,
    pipelineFor: pipelineFor,
    pipelineCount: pipelineCount,
    predicates: {
      statusOf: statusOf, isBooked: isBooked, sizeConfirmed: sizeConfirmed,
      everyTierHasFlavour: everyTierHasFlavour, tiersMissingFlavour: tiersMissingFlavour,
      flavoursDone: flavoursDone,
      structureDone: structureDone, quoteDone: quoteDone, batchDone: batchDone,
      bookedDone: bookedDone, batchSig: batchSig, batchHasFigures: batchHasFigures,
      batchIsStale: batchIsStale
    },
    standardLayers: standardLayers,
    getActiveBooking: getActiveBooking,
    setActiveBooking: setActiveBooking,
    resolveScope: resolveScope,
    displayGated: displayGated,   /* r57: the ONE gate test, shared by every surface */
    readCake: readCake,
    readScratch: readScratch,
    writeCake: writeCake,
    writeOccasion: writeOccasion,
    writeScratch: writeScratch,
    createInquiry: createInquiry,
    /* r201: doors ask these BEFORE opening a form, so the free tier meets
       the offer instead of a button that fails. */
    atCap: atCap,
    fileCap: fileCap,
    fileCount: fileCount,
    onChange: onChange,
    TIER_PRESETS: TIER_PRESETS
  };
  window.LAYERD = window.LAYERD || {};
  window.LAYERD.cakefile = api;
  window.LdCakeFile = api;   /* the brief's name */
})();